import type { DossierEventOutput, PortalViewOutput } from '@garage/shared';
import { formatDateTime } from '../../garage/lib/format.js';
import { buttonClasses } from '../../ui/Button.js';
import { cx } from '../../ui/cx.js';
import { CameraIcon, CheckCircleIcon, FolderIcon, InfoIcon } from '../../ui/icons.js';
import { tabOfTarget, usePortalNav } from '../nav.js';
import { useTz } from '../timezone.js';

type Message = PortalViewOutput['messages'][number];

/** Bloc visé par le bouton d'un événement (onglet puis défilement, aucune adresse modifiée). */
export function eventAnchor(event: DossierEventOutput, complementId: string | null): string | null {
  if (!event.action) return null;
  if (event.action.target === 'complement') return complementId ? `travaux-${complementId}` : 'decision';
  if (event.action.target === 'photos') return 'photos';
  return 'fiche-reception';
}

/**
 * Carte d'événement du dossier côté client (vague 6, ajout 5) : mise en avant, distincte des bulles
 * (pleine largeur, icône, phrase du serveur sans aucun montant), bouton vers les travaux
 * complémentaires (« Consulter et décider »), les photos ou la fiche de réception.
 * « Votre véhicule est prêt » : carte de succès.
 */
export function PortalEventCard({ message }: { message: Message }) {
  const tz = useTz();
  const nav = usePortalNav();
  const event = message.event;
  if (!event) return null;
  const ready = event.type === 'vehicle_ready';
  const anchor = eventAnchor(event, message.complement?.id ?? null);
  const icon =
    event.type === 'photos_published' ? <CameraIcon size={20} /> : ready ? <CheckCircleIcon size={20} /> : event.type === 'complement_sent' ? <FolderIcon size={20} /> : <InfoIcon size={20} />;
  return (
    <li data-kind="event" className="list-none">
      <div className={cx('flex items-start gap-3 rounded-2xl border p-(--card-p)', ready ? 'border-success-line bg-success-bg text-success-fg' : 'border-info-line bg-info-bg text-info-fg')}>
        <span aria-hidden className="mt-0.5 shrink-0">
          {icon}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-base font-medium">{ready ? 'Votre véhicule est prêt' : event.text}</p>
          {ready ? <p className="text-base">Vous pouvez venir le chercher. Les conditions de retrait sont rappelées dans l’onglet Suivi.</p> : null}
          <p className="mt-0.5 text-sm opacity-80">{formatDateTime(message.createdAt, tz)}</p>
          {event.action && anchor ? (
            <button
              type="button"
              onClick={() =>
                nav ? nav.goTo(tabOfTarget(anchor), anchor) : document.getElementById(anchor)?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
              }
              className={buttonClasses({ variant: 'secondary', size: 'md', className: 'mt-3 w-full sm:w-auto' })}
            >
              {event.action.label}
            </button>
          ) : null}
        </div>
      </div>
    </li>
  );
}
